import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function LimitingLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Limiting in Music Production: What It Is and How It Works",
      description:
        "Learn what a limiter does, how limiting prevents clipping, the difference between a limiter and compressor, and how limiting is used in mixing, mastering, and loudness control.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/limiting",
      articleSection: "Audio Education",
      keywords: [
        "limiting",
        "audio limiter",
        "what does a limiter do",
        "limiter vs compressor",
        "mastering limiter",
        "peak limiting",
        "brickwall limiter",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A limiter controls peaks and prevents a signal from going above a set ceiling, helping avoid clipping and manage loudness.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Limiting helps keep audio clean, controlled, and competitively loud without letting sudden transients overshoot.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Mastering chains, mix buses, drums, vocals, bass, broadcast audio, live sound, and anywhere peak control is important.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const limiterTypes = [
    {
      name: "Peak Limiter",
      description:
        "Designed to catch fast peaks and keep signals from exceeding a threshold or ceiling, often with very fast attack behavior.",
    },
    {
      name: "Brickwall Limiter",
      description:
        "A stricter form of limiting intended to stop peaks from going past the output ceiling, commonly used at the end of a mastering chain.",
    },
    {
      name: "Lookahead Limiter",
      description:
        "Uses a short preview of the signal so the limiter can react before a transient arrives, often producing cleaner and more transparent control.",
    },
    {
      name: "Program-Dependent Limiter",
      description:
        "Adapts its behavior to the material, which can sound smoother and more musical on complex mixes or changing dynamics.",
    },
    {
      name: "True Peak Limiter",
      description:
        "Designed to better control inter-sample peaks, which is especially useful for digital delivery formats and streaming platforms.",
    },
    {
      name: "Character / Color Limiter",
      description:
        "Adds some tonal coloration or saturation while limiting, trading strict transparency for a more stylized or punchy result.",
    },
  ];

  const limitingConcepts = [
    {
      name: "Threshold",
      description:
        "The level where limiting begins. When the signal tries to go above this point, the limiter turns it down.",
    },
    {
      name: "Ceiling / Output",
      description:
        "The maximum output level you want the signal to reach. This is often set slightly below 0 dBFS to reduce clipping risk.",
    },
    {
      name: "Gain Reduction",
      description:
        "The amount the limiter is turning the signal down in order to control peaks and stay under the ceiling.",
    },
    {
      name: "Transparency vs Loudness",
      description:
        "Heavier limiting can make a track louder, but too much can flatten transients, reduce punch, and make the audio feel strained.",
    },
  ];

  const controls = [
    {
      name: "Threshold",
      description:
        "Lowering the threshold makes the limiter work harder, causing more gain reduction and usually more apparent loudness control.",
    },
    {
      name: "Ceiling / Output Level",
      description:
        "Sets the maximum allowed output level. In mastering, this is often left a little below full scale for safety.",
    },
    {
      name: "Release",
      description:
        "Controls how quickly the limiter stops reducing gain after a peak passes. Too fast can sound edgy; too slow can sound squashed.",
    },
    {
      name: "Lookahead",
      description:
        "Lets the limiter react in advance to transients, which can improve control and reduce distortion at the cost of latency.",
    },
    {
      name: "Channel Link / Stereo Link",
      description:
        "Controls how the left and right channels influence each other, which can affect stereo stability and pumping behavior.",
    },
    {
      name: "Oversampling / True Peak",
      description:
        "Helps reduce digital artifacts and improves peak control, especially for final export and mastering applications.",
    },
  ];

  const commonUses = [
    "Preventing clipping on a mix bus or master bus",
    "Increasing perceived loudness in mastering",
    "Catching vocal peaks that jump out too aggressively",
    "Controlling drum transients without completely changing the groove",
    "Protecting broadcast, streaming, or live audio chains from overload",
    "Keeping exported audio within a safe digital ceiling",
  ];

  const bestPractices = [
    "Use your ears, not just the meter. A louder signal can sound impressive at first even when it is actually worse.",
    "Watch how much gain reduction is happening. A little limiting can be transparent, but too much often starts to flatten the mix.",
    "Set the output ceiling slightly below 0 dBFS when preparing digital releases.",
    "Do not expect a limiter to fix a bad mix. If the limiter is struggling, the real problem may be balance, EQ, or dynamics earlier in the chain.",
    "Pay attention to transients, punch, and vocal clarity. Those are often the first things to suffer when limiting becomes too heavy.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compression shapes dynamics more gradually and why limiting is often treated as a more extreme form of compression.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation adds harmonics and character, which is different from the strict peak control of limiting.",
    },
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "See how audible clipping and distortion differ from controlled limiting, even though both can reshape peaks.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Understand how EQ decisions can affect how hard a limiter has to work, especially in the low end and upper mids.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Limiting | Parish Audio Learning"
        description="Learn what a limiter does, how limiting prevents clipping, the difference between a limiter and compressor, and how limiting is used in music production and mastering."
        canonical="https://parishaudio.com/learning/limiting"
        schema={articleSchema}
      />

      <Box ref={topContainer}
        sx={{
          width: "95%",
          maxWidth: 1100,
          mx: "auto",
          minHeight: height - 140,
        }}
      >
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Limiting in Music Production: What It Is and How It Works
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                Limiting is one of the most important tools for controlling peaks,
                preventing clipping, and managing loudness in digital audio. It is
                commonly used in mixing and mastering to keep signals under
                control, especially when a track needs to be loud without allowing
                sudden transients to overshoot the maximum level.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                In simple terms, a limiter is like a compressor with a very high
                ratio. Once the signal reaches the threshold, the limiter acts
                aggressively to stop it from going much higher. That makes it
                extremely useful for peak control, final loudness shaping, and
                protecting audio from clipping in digital systems.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      ...sectionCardSx,
                      backgroundColor: panelBg,
                      boxShadow: 6,
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 700,
                            mb: 0.5,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.7,
                            fontSize: "0.97rem",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What is a limiter?
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                A limiter is a dynamics processor designed to stop audio from
                exceeding a chosen level. It reduces peaks once they cross a
                threshold, helping keep the output under control. In digital
                audio, this is especially important because signals that go above
                the maximum level can clip, which often creates harsh unwanted
                distortion.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                While compressors often shape dynamics in a more gradual or
                flexible way, limiters are usually used when strict control is
                needed. That is why limiting is so common on master buses,
                broadcast chains, and other places where preventing overs is a
                priority.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Limiting can be subtle or obvious. Light limiting may simply catch
                the occasional transient. Heavy limiting can dramatically raise
                average loudness, but it can also reduce punch, flatten dynamics,
                and change the character of the material.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How limiting works
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {limitingConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Typography
                        sx={{
                          color: accent,
                          fontWeight: 700,
                          mb: 0.75,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                Imagine a snare hit or vocal peak that suddenly jumps higher than
                the rest of the signal. A limiter reacts to that spike and reduces
                it so the output stays within bounds. If the limiter only catches
                occasional peaks, the effect can be nearly invisible. If it is
                working constantly, the sound often becomes denser, flatter, and
                less dynamic.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common types of limiters
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {limiterTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        boxShadow: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          color: style.color4,
                          fontWeight: 700,
                          mb: 0.75,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Limiter vs compressor
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Similar family, different purpose
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A limiter is usually considered a more extreme form of
                    compression. Both tools reduce dynamic range, but a limiter
                    tends to use a very high ratio and much stricter peak control.
                    Compressors are often chosen for shaping groove, tone, and
                    movement. Limiters are more often chosen for catching peaks
                    and setting a ceiling.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In practice, a compressor may be used earlier in the chain to
                    shape dynamics musically, while a limiter is placed later to
                    control the final peaks. The two often work together rather
                    than replacing each other.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common limiter controls
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 700,
                              mb: 0.5,
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How limiting is used in mixing and mastering
              </Typography>

              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  backgroundColor: panelBg,
                }}
              >
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack
                      key={item}
                      direction="row"
                      spacing={1.1}
                      alignItems="flex-start"
                    >
                      <LibraryMusicRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                On individual tracks, limiting can help catch unruly peaks that a
                compressor is not quite handling the way you want. On a mix bus or
                mastering chain, limiting is often used as the final stage that
                keeps the whole track under control while increasing perceived
                loudness. The key is to use only as much as the material can take
                before it starts to lose life.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                When limiting goes too far
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  <Stack direction="row" spacing={1.1} alignItems="flex-start">
                    <WarningAmberRoundedIcon
                      sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                    />
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Too much limiting can reduce punch, flatten transients, make
                      cymbals or vocals sound strained, and cause the whole mix to
                      feel smaller rather than bigger.
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1.1} alignItems="flex-start">
                    <WarningAmberRoundedIcon
                      sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                    />
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Heavy limiting may also create pumping, distortion, listener
                      fatigue, or a loss of depth, especially if the low end keeps
                      driving the limiter too hard.
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1.1} alignItems="flex-start">
                    <WarningAmberRoundedIcon
                      sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                    />
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Louder is not always better. A more open, dynamic mix often
                      feels more powerful than one that has been pushed past its
                      natural limit.
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices for using a limiter
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: style.color4,
                            fontWeight: 700,
                            mb: 0.75,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.75,
                            mb: 2,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.9,
                  mb: 2,
                }}
              >
                Limiting is one of the final safeguards in modern audio
                production. It helps control peaks, prevent clipping, and raise
                loudness when needed. Used carefully, it can make a mix feel
                polished and controlled without drawing attention to itself.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.9,
                }}
              >
                The key is balance. A limiter can protect your audio and improve
                presentation, but it can also damage punch and musicality when
                pushed too hard. Learning how much limiting a song can handle is
                one of the most important judgment calls in mixing and mastering.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}